import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../../../core/services/language.service';
import { ApiSitePublicService } from '../../../../../core/services/api/SitePublic/api-site-public';
import {
  SitePublicBlock,
  SitePublicImage,
  SitePublicProfileDetail,
} from '../../../../../core/models/site-public-profile.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ImageViewerComponent } from '../../../../../shared/components/image-viewer/image-viewer.component';
import { SiteAmenitiesComponent } from '../../../../../shared/components/site-amenities/site-amenities.component';
import { SITE_IMAGE_CATEGORY_LABELS } from '../../../../../core/utils/site-amenity.util';
import { ApiNewsService } from '../../../../../core/services/api/News/api-news';
import { PublicNewsListItem } from '../../../../../core/models/news.model';

interface GalleryTab {
  category: string;
  images: SitePublicImage[];
}

@Component({
  selector: 'app-parking-site-detail',
  standalone: true,
  imports: [CommonModule, PssLoadingComponent, ImageViewerComponent, SiteAmenitiesComponent],
  templateUrl: './parking-site-detail.component.html',
  styleUrl: './parking-site-detail.component.css',
})
export class ParkingSiteDetailComponent implements OnInit, OnDestroy {
  currentLang: 'TH' | 'EN' = 'TH';
  sub!: Subscription;

  detail: SitePublicProfileDetail | null = null;
  isLoading = false;
  notFound = false;
  viewerImages: string[] = [];
  viewerIndex = 0;
  isViewerOpen = false;
  sectionGroups: { section: string; blocks: SitePublicBlock[] }[] = [];

  readonly sectionOrder = ['overview', 'hourly_rate', 'monthly', 'other'] as const;

  constructor(
    private languageService: LanguageService,
    private apiSitePublic: ApiSitePublicService,
    private route: ActivatedRoute,
    private router: Router,
    private apiNews: ApiNewsService,
  ) {
    this.sub = this.languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });
  }

  /*#################################################### Function ################################################*/

  ngOnInit(): void {
    const ref = String(this.route.snapshot.paramMap.get('ref') || '').trim();
    if (!ref) {
      this.notFound = true;
      return;
    }
    this.publicRef = ref;
    this.loadDetail(ref);
    this.loadSiteNews(ref);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    this.router.navigate(['/home']);
  }

  /** ปก + gallery + รูปทุกบล็อก ตามลำดับที่แสดงบนหน้า */
  get galleryImages(): string[] {
    if (!this.detail) return [];
    const list: string[] = [...this.heroImages];
    for (const group of this.sectionGroups) {
      for (const block of group.blocks) {
        if (block.blockType === 'image' && block.image) list.push(block.image);
      }
    }
    return Array.from(new Set(list));
  }

  openZoom(url?: string | null): void {
    if (!url) return;
    const images = this.galleryImages;
    const index = images.indexOf(url);
    this.viewerImages = index >= 0 ? images : [url];
    this.viewerIndex = Math.max(0, index);
    this.isViewerOpen = true;
  }

  closeZoom(): void {
    this.isViewerOpen = false;
  }

  get title(): string {
    if (!this.detail) return '';
    if (this.currentLang === 'EN' && this.detail.titleEn?.trim()) {
      return this.detail.titleEn;
    }
    return this.detail.titleTh || this.detail.siteName;
  }

  get summary(): string {
    if (!this.detail) return '';
    if (this.currentLang === 'EN' && this.detail.summaryEn?.trim()) {
      return this.detail.summaryEn;
    }
    return this.detail.summaryTh || '';
  }

  get address(): string {
    if (!this.detail) return '';
    if (this.currentLang === 'EN' && this.detail.addressEn?.trim()) {
      return this.detail.addressEn;
    }
    return this.detail.addressTh || '';
  }

  sectionLabel(section: string): string {
    const key = (section || 'other').toLowerCase();
    const th: Record<string, string> = {
      overview: 'ภาพรวม',
      hourly_rate: 'อัตราค่าจอดรายชั่วโมง',
      monthly: 'จอดรายเดือน',
      other: 'อื่นๆ',
    };
    const en: Record<string, string> = {
      overview: 'Overview',
      hourly_rate: 'Hourly rates',
      monthly: 'Monthly parking',
      other: 'Other',
    };
    return this.currentLang === 'EN' ? (en[key] || key) : (th[key] || key);
  }

  blockTitle(block: SitePublicBlock): string {
    if (this.currentLang === 'EN' && block.titleEn?.trim()) return block.titleEn;
    return block.titleTh || '';
  }

  blockBody(block: SitePublicBlock): string {
    if (this.currentLang === 'EN' && block.bodyEn?.trim()) return block.bodyEn;
    return block.bodyTh || '';
  }

  trackByGroup(_: number, group: { section: string }): string {
    return group.section;
  }

  trackByBlock(index: number, block: SitePublicBlock): number {
    return block.id || index;
  }

  /** จัดกลุ่ม blocks ตาม section ตามลำดับที่กำหนด — ห้ามเปลี่ยนเป็น getter (ngFor จะสร้าง DOM ใหม่ทุกรอบ ทำให้กดรูปไม่ติด) */
  buildSectionGroups(detail: SitePublicProfileDetail | null): { section: string; blocks: SitePublicBlock[] }[] {
    if (!detail?.blocks?.length) return [];
    const active = detail.blocks
      .filter((b) => b.isActive !== false)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const bySection = new Map<string, SitePublicBlock[]>();
    for (const b of active) {
      const key = (b.section || 'other').toLowerCase();
      if (!bySection.has(key)) bySection.set(key, []);
      bySection.get(key)!.push(b);
    }

    const groups: { section: string; blocks: SitePublicBlock[] }[] = [];
    for (const s of this.sectionOrder) {
      const list = bySection.get(s);
      if (list?.length) {
        groups.push({ section: s, blocks: list });
        bySection.delete(s);
      }
    }
    for (const [section, blocks] of bySection) {
      if (blocks.length) groups.push({ section, blocks });
    }
    return groups;
  }

  /*############################################# API #################################################*/

  loadDetail(publicRef: string): void {
    this.isLoading = true;
    this.notFound = false;
    this.apiSitePublic.getPublicParkingSiteDetail(publicRef).subscribe({
      next: (res) => {
        this.detail = res;
        this.sectionGroups = this.buildSectionGroups(res);
        this.heroImages = this.buildHeroImages(res);
        this.heroIndex = 0;
        this.galleryTabs = this.buildGalleryTabs(res);
        this.activeGalleryCategory = this.galleryTabs[0]?.category ?? '';
        this.isLoading = false;
      },
      error: (err) => {
        console.error('getPublicParkingSiteDetail failed', err);
        this.detail = null;
        this.sectionGroups = [];
        this.heroImages = [];
        this.galleryTabs = [];
        this.notFound = true;
        this.isLoading = false;
      },
    });
  }

  /*#################################################### Gallery ################################################*/

  heroImages: string[] = [];
  heroIndex = 0;
  galleryTabs: GalleryTab[] = [];
  activeGalleryCategory = '';

  get activeGalleryImages(): SitePublicImage[] {
    return this.galleryTabs.find((t) => t.category === this.activeGalleryCategory)?.images ?? [];
  }

  onHeroScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el.clientWidth) return;
    this.heroIndex = Math.round(el.scrollLeft / el.clientWidth);
  }

  selectGalleryCategory(category: string): void {
    this.activeGalleryCategory = category;
  }

  galleryCategoryLabel(category: string): string {
    const label = SITE_IMAGE_CATEGORY_LABELS[category];
    if (!label) return category;
    return this.currentLang === 'EN' ? label.en : label.th;
  }

  imageCaption(image: SitePublicImage): string {
    if (this.currentLang === 'EN' && image.captionEn?.trim()) return image.captionEn;
    return image.captionTh || '';
  }

  trackByUrl(_: number, url: string): string {
    return url;
  }

  trackByTab(_: number, tab: GalleryTab): string {
    return tab.category;
  }

  trackByImage(index: number, image: SitePublicImage): string {
    return image.image || String(index);
  }

  /** ปก → gallery ตามลำดับ (cache ไว้ ไม่ใช้ getter กับ ngFor) */
  buildHeroImages(detail: SitePublicProfileDetail | null): string[] {
    if (!detail) return [];
    const list: string[] = [];
    if (detail.coverImage) list.push(detail.coverImage);
    for (const img of detail.images ?? []) {
      if (img.image) list.push(img.image);
    }
    return list;
  }

  buildGalleryTabs(detail: SitePublicProfileDetail | null): GalleryTab[] {
    if (!detail?.images?.length) return [];
    const order = Object.keys(SITE_IMAGE_CATEGORY_LABELS);
    const byCategory = new Map<string, SitePublicImage[]>();
    for (const img of detail.images) {
      const key = (img.category || 'parking').toLowerCase();
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key)!.push(img);
    }
    return Array.from(byCategory.entries())
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([category, images]) => ({ category, images }));
  }

  /*#################################################### Site news ################################################*/

  private static readonly SiteNewsLimit = 5;

  publicRef = '';
  siteNews: PublicNewsListItem[] = [];
  siteNewsTotal = 0;

  loadSiteNews(publicRef: string): void {
    this.apiNews.getPublicSiteNews(publicRef, 1, ParkingSiteDetailComponent.SiteNewsLimit).subscribe({
      next: (res) => {
        console.log('getPublicSiteNews', res);
        this.siteNews = res?.items ?? [];
        this.siteNewsTotal = res?.totalCount ?? this.siteNews.length;
      },
      error: (err) => {
        console.error('getPublicSiteNews failed', err);
        this.siteNews = [];
        this.siteNewsTotal = 0;
      },
    });
  }

  newsTitle(news: PublicNewsListItem): string {
    if (this.currentLang === 'EN' && news.titleEn?.trim()) return news.titleEn;
    return news.title;
  }

  openNews(id: number): void {
    this.router.navigate(['/news-detail', id]);
  }

  openAllSiteNews(): void {
    if (!this.publicRef) return;
    this.router.navigate(['/news'], { queryParams: { lot: this.publicRef } });
  }

  trackByNews(_: number, news: PublicNewsListItem): number {
    return news.id;
  }
}
